import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "amount", "paidOn", "currency", "turboFrameSrc"]
  static values = {
    previewUrl: String,
    delay: { type: Number, default: 250 }
  }

  #timer = null
  #choice = null
  #focusId = null

  connect() {
    if (this.hasTurboFrameSrcTarget) {
      this.turboFrameSrcTarget.value = window.location.href
    }

    this.element.addEventListener("turbo:frame-render", this.#restoreFocus)
  }

  disconnect() {
    clearTimeout(this.#timer)
    this.element.removeEventListener("turbo:frame-render", this.#restoreFocus)
  }

  update() {
    if (!this.hasFrameTarget || !this.hasPreviewUrlValue) return

    // A different amount, date or currency changes which documents are on offer, so an earlier pick no longer stands.
    this.#choice = null
    clearTimeout(this.#timer)
    this.#timer = setTimeout(() => {
      this.#refreshFrame()
    }, this.delayValue)
  }

  // The user picked a document in "Zmeniť, čo sa vystaví": the server re-renders the panel with that document as the
  // ticked row, so the row says what the form will post.
  choose(event) {
    if (!this.hasFrameTarget || !this.hasPreviewUrlValue) return

    clearTimeout(this.#timer)
    this.#choice = event.target.value
    // The refresh replaces the radio the user is on; without this a keyboard user loses their place after every arrow.
    this.#focusId = event.target.id || null
    this.#refreshFrame()
  }

  // The primary row's box and the "nothing" radio say opposite things when both are set, and a post would honour "nothing".
  // Ticking the box again therefore means "issue that document after all": the panel is re-rendered with it as the pick.
  primary(event) {
    if (!event.target.checked || !this.hasFrameTarget || !this.hasPreviewUrlValue) return

    const action = event.target.dataset.primaryAction
    const picked = this.element.querySelector('input[type="radio"][name$="[action]"]:checked')
    if (!action || picked?.value !== "none") return

    clearTimeout(this.#timer)
    this.#choice = action
    this.#focusId = event.target.id || null
    this.#refreshFrame()
  }

  #restoreFocus = () => {
    if (!this.#focusId) return

    document.getElementById(this.#focusId)?.focus()
    this.#focusId = null
  }

  #refreshFrame() {
    if (!this.hasFrameTarget || !this.hasPreviewUrlValue) return

    const base = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "http://localhost"
    const url = new URL(this.previewUrlValue, base)

    if (this.hasAmountTarget) {
      url.searchParams.set("amount", this.amountTarget.value)
    }
    if (this.hasPaidOnTarget) {
      url.searchParams.set("paid_on", this.paidOnTarget.value)
    }
    if (this.hasCurrencyTarget) {
      url.searchParams.set("currency", this.currencyTarget.value)
    }

    if (this.#choice) {
      url.searchParams.set("choice", this.#choice)
    }

    this.frameTarget.src = url.toString()
  }
}
